import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { 
  Home, Users, MapPin, FileText, Camera, Send,
  Plus, RefreshCw, AlertTriangle, Calendar, Phone, Mail,
  ClipboardList, DollarSign, Heart, Search, Map,
  ChevronDown, Edit2, Eye, Trash2, Check, X,
  TrendingUp, Clock, Filter, Star, MessageSquare,
  Sparkles, Truck, CreditCard, LayoutDashboard,
  Baby, Activity, Stethoscope, FileCheck, Syringe, Scale, Clipboard
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Horse icon component
const HorseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 8c-1.5 1-3.5 1-5 0-1.5-1-3.5-1-5 0s-3.5 1-5 0c-1.5-1-3.5-1-5 0"/>
    <path d="M5.5 18.5 2 22"/>
    <path d="m18.5 18.5 3.5 3.5"/>
    <path d="M12 22v-8"/>
    <path d="M8 22V10"/>
    <path d="M16 22V10"/>
    <path d="M12 14c-2-2-2-4 0-6s4 0 4 2"/>
    <path d="M12 8V2l2 2"/>
  </svg>
);

export default function ChevauxModule() {
  const { activeSociete } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const activeTab = searchParams.get('tab') || 'dashboard';
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [dashboard, setDashboard] = useState({});
  const [chevaux, setChevaux] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [hebergements, setHebergements] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [factures, setFactures] = useState([]);
  const [debourrageChevaux, setDebourrageChevaux] = useState([]);
  const [attelageFormations, setAttelageFormations] = useState([]);
  
  // Modal states
  const [showAddCheval, setShowAddCheval] = useState(false);
  const [showAddProspect, setShowAddProspect] = useState(false);
  const [selectedCheval, setSelectedCheval] = useState(null);
  
  // Form states
  const [newCheval, setNewCheval] = useState({
    nom: '', sire: '', race: '', robe: '', sexe: 'hongre',
    proprietaire_nom: '', proprietaire_prenom: '', proprietaire_telephone: '', proprietaire_email: '',
    type_pension: 'box', box_numero: '', date_entree: new Date().toISOString().split('T')[0],
    tarif_mensuel: 350, statut: 'pension'
  });

  const [newProspect, setNewProspect] = useState({
    nom: '', prenom: '', telephone: '', email: '',
    cheval_nom: '', type_pension_souhaite: 'box', source: 'bouche_a_oreille', notes: ''
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 
      'Authorization': `Bearer ${token}`,
      'X-Societe-Id': activeSociete?.id || ''
    };

    try {
      // Fetch pension dashboard
      const dashRes = await fetch(`${API_URL}/api/pension/dashboard`, { headers });
      if (dashRes.ok) setDashboard(await dashRes.json());

      // Fetch chevaux
      const chevauxRes = await fetch(`${API_URL}/api/pension/chevaux`, { headers });
      if (chevauxRes.ok) {
        const data = await chevauxRes.json();
        setChevaux(data.chevaux || []);
      }

      // Fetch prospects
      const prospectsRes = await fetch(`${API_URL}/api/pension/prospects`, { headers });
      if (prospectsRes.ok) {
        const data = await prospectsRes.json();
        setProspects(data.prospects || []);
      }

      // Fetch hébergements
      const hebergementsRes = await fetch(`${API_URL}/api/pension/hebergements`, { headers });
      if (hebergementsRes.ok) {
        const data = await hebergementsRes.json();
        setHebergements(data.hebergements || []);
      }

      // Fetch contrats
      const contratsRes = await fetch(`${API_URL}/api/pension/contrats`, { headers });
      if (contratsRes.ok) {
        const data = await contratsRes.json();
        setContrats(data.contrats || []);
      }

      // Fetch factures
      const facturesRes = await fetch(`${API_URL}/api/pension/factures`, { headers });
      if (facturesRes.ok) {
        const data = await facturesRes.json();
        setFactures(data.factures || []);
      }

      // Fetch débourrage
      const debourrageRes = await fetch(`${API_URL}/api/chevaux/debourrage`, { headers });
      if (debourrageRes.ok) {
        const data = await debourrageRes.json();
        setDebourrageChevaux(data.chevaux || []);
      }

      // Fetch attelage
      const attelageRes = await fetch(`${API_URL}/api/chevaux/attelage`, { headers });
      if (attelageRes.ok) {
        const data = await attelageRes.json();
        setAttelageFormations(data.formations || []);
      }

    } catch (error) {
      console.error('Erreur chargement données chevaux:', error);
    }
    setLoading(false);
  }, [activeSociete]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create cheval
  const handleCreateCheval = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/pension/inscription-complete`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify({
          ...newCheval,
          cheval_nom: newCheval.nom,
          cheval_sire: newCheval.sire,
          cheval_race: newCheval.race,
          cheval_robe: newCheval.robe,
          cheval_sexe: newCheval.sexe
        })
      });
      
      if (res.ok) {
        setShowAddCheval(false);
        setNewCheval({
          nom: '', sire: '', race: '', robe: '', sexe: 'hongre',
          proprietaire_nom: '', proprietaire_prenom: '', proprietaire_telephone: '', proprietaire_email: '',
          type_pension: 'box', box_numero: '', date_entree: new Date().toISOString().split('T')[0],
          tarif_mensuel: 350, statut: 'pension'
        });
        fetchData();
      }
    } catch (error) {
      console.error('Erreur création cheval:', error);
    }
  };

  // Create prospect
  const handleCreateProspect = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/pension/prospects`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify(newProspect)
      });
      
      if (res.ok) {
        setShowAddProspect(false);
        setNewProspect({
          nom: '', prenom: '', telephone: '', email: '',
          cheval_nom: '', type_pension_souhaite: 'box', source: 'bouche_a_oreille', notes: ''
        });
        fetchData();
      }
    } catch (error) {
      console.error('Erreur création prospect:', error);
    }
  };

  // Filter chevaux by search
  const filteredChevaux = chevaux.filter(c => 
    c.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.proprietaire_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.race?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats calculation
  const stats = {
    totalChevaux: chevaux.length,
    enPension: chevaux.filter(c => c.statut === 'pension' || c.type_pension).length,
    enDebourrage: debourrageChevaux.length || chevaux.filter(c => c.statut === 'debourrage').length,
    enAttelage: attelageFormations.length || chevaux.filter(c => c.statut === 'attelage').length,
    boxesOccupes: hebergements.filter(h => h.type === 'box' && h.cheval_id).length,
    boxesLibres: hebergements.filter(h => h.type === 'box' && !h.cheval_id).length,
    presOccupes: hebergements.filter(h => h.type === 'pre' && h.cheval_id).length,
    facturesImpayees: factures.filter(f => f.statut === 'en_attente' || f.statut === 'impayee').length,
    caTotal: factures.filter(f => f.statut === 'payee').reduce((sum, f) => sum + (f.montant || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
        <span className="ml-2 text-gray-600">Chargement du module Chevaux...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <HorseIcon className="h-8 w-8 text-amber-600" />
            Gestion des Chevaux
          </h1>
          <p className="text-gray-500 mt-1">
            Pension, débourrage, attelage, élevage - tout en un seul endroit
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Dialog open={showAddCheval} onOpenChange={setShowAddCheval}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-4 w-4 mr-2" />
                Nouveau cheval
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Ajouter un cheval</DialogTitle>
                <DialogDescription>
                  Enregistrez un nouveau cheval avec les informations du propriétaire
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom du cheval *</Label>
                    <Input 
                      value={newCheval.nom} 
                      onChange={(e) => setNewCheval({...newCheval, nom: e.target.value})}
                      placeholder="Ex: Tornado"
                    />
                  </div>
                  <div>
                    <Label>N° SIRE</Label>
                    <Input 
                      value={newCheval.sire} 
                      onChange={(e) => setNewCheval({...newCheval, sire: e.target.value})}
                      placeholder="Ex: 12345678A"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Race</Label>
                    <Input 
                      value={newCheval.race} 
                      onChange={(e) => setNewCheval({...newCheval, race: e.target.value})}
                      placeholder="Ex: Selle Français"
                    />
                  </div>
                  <div>
                    <Label>Robe</Label>
                    <Input 
                      value={newCheval.robe} 
                      onChange={(e) => setNewCheval({...newCheval, robe: e.target.value})}
                      placeholder="Ex: Bai"
                    />
                  </div>
                  <div>
                    <Label>Sexe</Label>
                    <Select value={newCheval.sexe} onValueChange={(v) => setNewCheval({...newCheval, sexe: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hongre">Hongre</SelectItem>
                        <SelectItem value="jument">Jument</SelectItem>
                        <SelectItem value="etalon">Étalon</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Propriétaire</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nom *</Label>
                      <Input 
                        value={newCheval.proprietaire_nom} 
                        onChange={(e) => setNewCheval({...newCheval, proprietaire_nom: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Prénom</Label>
                      <Input 
                        value={newCheval.proprietaire_prenom} 
                        onChange={(e) => setNewCheval({...newCheval, proprietaire_prenom: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Téléphone *</Label>
                      <Input 
                        value={newCheval.proprietaire_telephone} 
                        onChange={(e) => setNewCheval({...newCheval, proprietaire_telephone: e.target.value})}
                        placeholder="06 XX XX XX XX"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={newCheval.proprietaire_email} 
                        onChange={(e) => setNewCheval({...newCheval, proprietaire_email: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Hébergement</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Type</Label>
                      <Select value={newCheval.type_pension} onValueChange={(v) => setNewCheval({...newCheval, type_pension: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="box">Box</SelectItem>
                          <SelectItem value="pre">Pré</SelectItem>
                          <SelectItem value="box_pre">Box + Pré</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>N° Box / Pré</Label>
                      <Input 
                        value={newCheval.box_numero} 
                        onChange={(e) => setNewCheval({...newCheval, box_numero: e.target.value})}
                        placeholder="Ex: B12"
                      />
                    </div>
                    <div>
                      <Label>Tarif mensuel (€)</Label>
                      <Input 
                        type="number"
                        value={newCheval.tarif_mensuel} 
                        onChange={(e) => setNewCheval({...newCheval, tarif_mensuel: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddCheval(false)}>Annuler</Button>
                <Button onClick={handleCreateCheval} className="bg-amber-600 hover:bg-amber-700">
                  Enregistrer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-8 h-auto p-1 bg-gray-100">
          <TabsTrigger value="dashboard" className="flex items-center gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <LayoutDashboard className="h-4 w-4" />
            <span className="hidden sm:inline">Tableau de bord</span>
          </TabsTrigger>
          <TabsTrigger value="pension" className="flex items-center gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <Home className="h-4 w-4" />
            <span className="hidden sm:inline">Pension</span>
          </TabsTrigger>
          <TabsTrigger value="debourrage" className="flex items-center gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Débourrage</span>
          </TabsTrigger>
          <TabsTrigger value="attelage" className="flex items-center gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <Truck className="h-4 w-4" />
            <span className="hidden sm:inline">Attelage</span>
          </TabsTrigger>
          <TabsTrigger value="elevage" className="flex items-center gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <Heart className="h-4 w-4" />
            <span className="hidden sm:inline">Élevage</span>
          </TabsTrigger>
          <TabsTrigger value="boxes" className="flex items-center gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <MapPin className="h-4 w-4" />
            <span className="hidden sm:inline">Boxes & Prés</span>
          </TabsTrigger>
          <TabsTrigger value="crm" className="flex items-center gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">CRM</span>
          </TabsTrigger>
          <TabsTrigger value="facturation" className="flex items-center gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Facturation</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-700">Total Chevaux</p>
                    <p className="text-3xl font-bold text-amber-900">{stats.totalChevaux}</p>
                  </div>
                  <HorseIcon className="h-10 w-10 text-amber-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700">En Pension</p>
                    <p className="text-3xl font-bold text-green-900">{stats.enPension}</p>
                  </div>
                  <Home className="h-10 w-10 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-700">En Débourrage</p>
                    <p className="text-3xl font-bold text-purple-900">{stats.enDebourrage}</p>
                  </div>
                  <Sparkles className="h-10 w-10 text-purple-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700">Formation Attelage</p>
                    <p className="text-3xl font-bold text-blue-900">{stats.enAttelage}</p>
                  </div>
                  <Truck className="h-10 w-10 text-blue-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Hébergements & CA */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-amber-600" />
                  Boxes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-green-600">{stats.boxesOccupes} occupés</span>
                  <span className="text-gray-400">{stats.boxesLibres} libres</span>
                </div>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${(stats.boxesOccupes / (stats.boxesOccupes + stats.boxesLibres || 1)) * 100}%` }}
                  />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-amber-600" />
                  Facturation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-600">{stats.caTotal.toLocaleString()} €</p>
                <p className="text-sm text-gray-500">CA encaissé</p>
                {stats.facturesImpayees > 0 && (
                  <Badge variant="destructive" className="mt-2">
                    {stats.facturesImpayees} facture(s) en attente
                  </Badge>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-amber-600" />
                  Prospects
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{prospects.length}</p>
                <p className="text-sm text-gray-500">En attente de contact</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setActiveTab('crm')}
                >
                  Voir CRM
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Liste des chevaux */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Tous les chevaux</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Rechercher..." 
                      className="pl-9 w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Race</TableHead>
                    <TableHead>Propriétaire</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Hébergement</TableHead>
                    <TableHead>Tarif/mois</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredChevaux.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Aucun cheval enregistré
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredChevaux.map((cheval) => (
                      <TableRow key={cheval.id} className="hover:bg-amber-50">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <HorseIcon className="h-5 w-5 text-amber-600" />
                            {cheval.nom}
                          </div>
                        </TableCell>
                        <TableCell>{cheval.race || '-'}</TableCell>
                        <TableCell>
                          <div>
                            <p>{cheval.proprietaire_nom} {cheval.proprietaire_prenom}</p>
                            <p className="text-xs text-gray-500">{cheval.proprietaire_telephone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            cheval.statut === 'debourrage' ? 'secondary' :
                            cheval.statut === 'attelage' ? 'outline' :
                            'default'
                          } className={
                            cheval.statut === 'pension' || cheval.type_pension ? 'bg-green-100 text-green-800' : ''
                          }>
                            {cheval.statut || 'Pension'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {cheval.type_pension === 'box' ? 'Box' : 
                             cheval.type_pension === 'pre' ? 'Pré' : 
                             cheval.type_pension === 'box_pre' ? 'Box + Pré' : 
                             cheval.box_numero || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>{cheval.tarif_mensuel || 350} €</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedCheval(cheval)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pension Tab */}
        <TabsContent value="pension" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-5 w-5 text-amber-600" />
                Chevaux en pension
              </CardTitle>
              <CardDescription>Gestion des pensionnaires</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cheval</TableHead>
                    <TableHead>Propriétaire</TableHead>
                    <TableHead>Box/Pré</TableHead>
                    <TableHead>Date entrée</TableHead>
                    <TableHead>Tarif</TableHead>
                    <TableHead>Contrat</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chevaux.filter(c => c.type_pension || c.statut === 'pension').map((cheval) => (
                    <TableRow key={cheval.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <HorseIcon className="h-5 w-5 text-amber-600" />
                          {cheval.nom}
                        </div>
                      </TableCell>
                      <TableCell>{cheval.proprietaire_nom} {cheval.proprietaire_prenom}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{cheval.box_numero || cheval.type_pension}</Badge>
                      </TableCell>
                      <TableCell>{cheval.date_entree ? new Date(cheval.date_entree).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell>{cheval.tarif_mensuel || 350} €/mois</TableCell>
                      <TableCell>
                        <Badge variant={cheval.contrat_actif ? 'default' : 'secondary'}>
                          {cheval.contrat_actif ? 'Actif' : 'À renouveler'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Débourrage Tab - AMÉLIORÉ */}
        <TabsContent value="debourrage" className="space-y-4">
          {/* Stats débourrage */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="text-sm text-purple-700">En débourrage</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {chevaux.filter(c => c.statut === 'debourrage').length || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Calendar className="h-8 w-8 text-gray-600" />
                  <div>
                    <p className="text-sm text-gray-600">Durée moyenne</p>
                    <p className="text-2xl font-bold">3 mois</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Check className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Terminés cette année</p>
                    <p className="text-2xl font-bold text-green-600">0</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-8 w-8 text-amber-600" />
                  <div>
                    <p className="text-sm text-gray-600">Tarif débourrage</p>
                    <p className="text-2xl font-bold">800 €/mois</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Programme de débourrage */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    Programme de Débourrage
                  </CardTitle>
                  <CardDescription>Étapes et suivi de progression</CardDescription>
                </div>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouveau débourrage
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Étapes du débourrage */}
              <div className="mb-6 p-4 bg-purple-50 rounded-lg">
                <h4 className="font-medium text-purple-900 mb-3">Étapes du programme :</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {['1. Désensibilisation', '2. Longe & Licol', '3. Sellage', '4. Montoir', '5. Premiers pas montés'].map((etape, idx) => (
                    <div key={idx} className="p-2 bg-white rounded border text-center text-sm">
                      {etape}
                    </div>
                  ))}
                </div>
              </div>

              {chevaux.filter(c => c.statut === 'debourrage').length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun cheval en débourrage actuellement</p>
                  <p className="text-sm mt-2">Les chevaux de pension peuvent être basculés en débourrage</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cheval</TableHead>
                      <TableHead>Propriétaire</TableHead>
                      <TableHead>Étape actuelle</TableHead>
                      <TableHead>Progression</TableHead>
                      <TableHead>Début</TableHead>
                      <TableHead>Rapport</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chevaux.filter(c => c.statut === 'debourrage').map((cheval) => (
                      <TableRow key={cheval.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <HorseIcon className="h-5 w-5 text-purple-600" />
                            {cheval.nom}
                          </div>
                        </TableCell>
                        <TableCell>{cheval.proprietaire_nom}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-purple-50">
                            {cheval.etape_debourrage || 'Désensibilisation'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full">
                              <div className="h-full bg-purple-500 rounded-full" style={{ width: `${cheval.progression || 20}%` }} />
                            </div>
                            <span className="text-sm">{cheval.progression || 20}%</span>
                          </div>
                        </TableCell>
                        <TableCell>{cheval.date_debut_debourrage || '-'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <FileText className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attelage Tab - AMÉLIORÉ */}
        <TabsContent value="attelage" className="space-y-4">
          {/* Stats attelage */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Truck className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-sm text-blue-700">En formation</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {chevaux.filter(c => c.statut === 'attelage').length || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Activity className="h-8 w-8 text-gray-600" />
                  <div>
                    <p className="text-sm text-gray-600">Séances/semaine</p>
                    <p className="text-2xl font-bold">3</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Check className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Chevaux formés</p>
                    <p className="text-2xl font-bold text-green-600">0</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-8 w-8 text-amber-600" />
                  <div>
                    <p className="text-sm text-gray-600">Tarif formation</p>
                    <p className="text-2xl font-bold">600 €/mois</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Types de formations */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Sparkles className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Débourrage Attelage</h4>
                    <p className="text-sm text-gray-500">Initiation aux harnais</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Durée : 2-3 mois</p>
              </CardContent>
            </Card>
            <Card className="border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Truck className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Attelage Loisir</h4>
                    <p className="text-sm text-gray-500">Promenades & randonnées</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Durée : 1-2 mois</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Star className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Attelage Compétition</h4>
                    <p className="text-sm text-gray-500">Dressage & marathon</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Durée : 6+ mois</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-blue-600" />
                  Chevaux en Formation Attelage
                </CardTitle>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle formation
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {chevaux.filter(c => c.statut === 'attelage').length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Truck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucune formation attelage en cours</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cheval</TableHead>
                      <TableHead>Type formation</TableHead>
                      <TableHead>Progression</TableHead>
                      <TableHead>Prochaine séance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chevaux.filter(c => c.statut === 'attelage').map((cheval) => (
                      <TableRow key={cheval.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <HorseIcon className="h-5 w-5 text-blue-600" />
                            {cheval.nom}
                          </div>
                        </TableCell>
                        <TableCell>{cheval.type_attelage || 'Débourrage attelage'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${cheval.progression_attelage || 0}%` }} />
                            </div>
                            <span className="text-sm">{cheval.progression_attelage || 0}%</span>
                          </div>
                        </TableCell>
                        <TableCell>{cheval.prochaine_seance || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Élevage Tab */}
        <TabsContent value="elevage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-600" />
                Élevage & Reproduction
              </CardTitle>
              <CardDescription>Gestion des reproducteurs, saillies et naissances</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="bg-pink-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Heart className="h-8 w-8 text-pink-600" />
                      <div>
                        <p className="text-sm text-pink-700">Poulinières</p>
                        <p className="text-2xl font-bold">{chevaux.filter(c => c.sexe === 'jument').length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Activity className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="text-sm text-blue-700">Étalons</p>
                        <p className="text-2xl font-bold">{chevaux.filter(c => c.sexe === 'etalon').length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Baby className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-sm text-green-700">Poulains</p>
                        <p className="text-2xl font-bold">0</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="text-center py-4 text-gray-500">
                <p>Fonctionnalités d'élevage en cours de développement</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Boxes & Prés Tab */}
        <TabsContent value="boxes" className="space-y-4">
          {/* Bannière future fonctionnalité GPS */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <MapPin className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-blue-900">Localisation & Suivi GPS</h3>
                  <p className="text-sm text-blue-700">
                    Bientôt disponible : Suivi temps réel de vos chevaux avec trackers GPS (style AirTag)
                  </p>
                </div>
                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                  Prochainement
                </Badge>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Liste des boxes */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-amber-600" />
                      Boxes
                    </CardTitle>
                    <CardDescription>Hébergements individuels</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigate('/parcellaire?view=boxes')}>
                    <MapPin className="h-4 w-4 mr-2" />
                    Voir sur carte
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {chevaux.slice(0, 8).map((cheval, idx) => (
                    <div 
                      key={cheval.id || idx}
                      className={`p-3 rounded-lg border-2 ${cheval.nom ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-2">
                        <Home className={`h-5 w-5 ${cheval.nom ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className="font-medium">Box {idx + 1}</span>
                      </div>
                      {cheval.nom && (
                        <div className="mt-2 flex items-center gap-2">
                          <HorseIcon className="h-4 w-4 text-amber-600" />
                          <span className="text-sm">{cheval.nom}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Liste des prés */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-green-600" />
                      Prés & Paddocks
                    </CardTitle>
                    <CardDescription>Pâturages sur le parcellaire</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigate('/parcellaire?view=pres')}>
                    <MapPin className="h-4 w-4 mr-2" />
                    Voir sur carte
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Prés liés aux parcelles "Prairie permanente" */}
                  <div className="p-4 rounded-lg border-2 border-green-300 bg-green-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-green-600" />
                        <span className="font-medium">Pré Principal</span>
                      </div>
                      <Badge className="bg-green-100 text-green-800">4.5 ha</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Prairie permanente - Saint-Sorlin-de-Vienne</p>
                    <div className="flex flex-wrap gap-1">
                      {chevaux.slice(0, 3).map((c) => (
                        <Badge key={c.id} variant="outline" className="text-xs">
                          <HorseIcon className="h-3 w-3 mr-1" />
                          {c.nom}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="p-4 rounded-lg border-2 border-amber-200 bg-amber-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-amber-600" />
                        <span className="font-medium">Paddock Entraînement</span>
                      </div>
                      <Badge className="bg-amber-100 text-amber-800">0.8 ha</Badge>
                    </div>
                    <p className="text-sm text-gray-600">Zone débourrage et travail</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Carte miniature */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Map className="h-5 w-5 text-amber-600" />
                  Localisation sur le Parcellaire
                </CardTitle>
                <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => navigate('/parcellaire')}>
                  <MapPin className="h-4 w-4 mr-2" />
                  Ouvrir le parcellaire complet
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="relative h-64 bg-gradient-to-br from-green-100 to-green-200 rounded-lg overflow-hidden">
                {/* Placeholder pour la carte */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <Map className="h-16 w-16 text-green-400 mx-auto mb-4" />
                    <p className="text-green-700 font-medium">Vue parcellaire</p>
                    <p className="text-green-600 text-sm">15 parcelles • 19.6 ha</p>
                    <Button 
                      variant="outline" 
                      className="mt-4 bg-white"
                      onClick={() => navigate('/parcellaire')}
                    >
                      Voir la carte interactive
                    </Button>
                  </div>
                </div>
                {/* Marqueurs simulés */}
                <div className="absolute top-8 left-12 animate-pulse">
                  <div className="bg-green-500 text-white p-1 rounded-full">
                    <HorseIcon className="h-4 w-4" />
                  </div>
                </div>
                <div className="absolute top-16 right-20 animate-pulse" style={{animationDelay: '0.5s'}}>
                  <div className="bg-amber-500 text-white p-1 rounded-full">
                    <HorseIcon className="h-4 w-4" />
                  </div>
                </div>
                <div className="absolute bottom-12 left-1/3 animate-pulse" style={{animationDelay: '1s'}}>
                  <div className="bg-blue-500 text-white p-1 rounded-full">
                    <HorseIcon className="h-4 w-4" />
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
                <div className="p-2 bg-green-50 rounded">
                  <span className="text-green-600 font-medium">7</span>
                  <p className="text-gray-500">En box</p>
                </div>
                <div className="p-2 bg-amber-50 rounded">
                  <span className="text-amber-600 font-medium">0</span>
                  <p className="text-gray-500">Au pré</p>
                </div>
                <div className="p-2 bg-blue-50 rounded">
                  <span className="text-blue-600 font-medium">0</span>
                  <p className="text-gray-500">GPS actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CRM Tab */}
        <TabsContent value="crm" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-amber-600" />
                    CRM Propriétaires & Prospects
                  </CardTitle>
                  <CardDescription>Gestion des contacts et suivi commercial</CardDescription>
                </div>
                <Dialog open={showAddProspect} onOpenChange={setShowAddProspect}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Nouveau prospect
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Ajouter un prospect</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Nom *</Label>
                          <Input 
                            value={newProspect.nom} 
                            onChange={(e) => setNewProspect({...newProspect, nom: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Prénom</Label>
                          <Input 
                            value={newProspect.prenom} 
                            onChange={(e) => setNewProspect({...newProspect, prenom: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Téléphone *</Label>
                          <Input 
                            value={newProspect.telephone} 
                            onChange={(e) => setNewProspect({...newProspect, telephone: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input 
                            type="email"
                            value={newProspect.email} 
                            onChange={(e) => setNewProspect({...newProspect, email: e.target.value})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Nom du cheval</Label>
                        <Input 
                          value={newProspect.cheval_nom} 
                          onChange={(e) => setNewProspect({...newProspect, cheval_nom: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Notes</Label>
                        <Textarea 
                          value={newProspect.notes} 
                          onChange={(e) => setNewProspect({...newProspect, notes: e.target.value})}
                          placeholder="Informations complémentaires..."
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddProspect(false)}>Annuler</Button>
                      <Button onClick={handleCreateProspect} className="bg-amber-600 hover:bg-amber-700">
                        Enregistrer
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Prospects */}
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    Prospects ({prospects.length})
                  </h3>
                  {prospects.length === 0 ? (
                    <p className="text-gray-500 text-sm">Aucun prospect en attente</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Contact</TableHead>
                          <TableHead>Cheval</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {prospects.map((prospect) => (
                          <TableRow key={prospect.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{prospect.nom} {prospect.prenom}</p>
                                <p className="text-sm text-gray-500">{prospect.telephone}</p>
                              </div>
                            </TableCell>
                            <TableCell>{prospect.cheval_nom || '-'}</TableCell>
                            <TableCell>{prospect.source || 'N/A'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{prospect.statut || 'Nouveau'}</Badge>
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm">
                                <Phone className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm">
                                <Mail className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>

                {/* Propriétaires actuels */}
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4 text-green-600" />
                    Propriétaires actuels ({[...new Set(chevaux.map(c => c.proprietaire_nom))].length})
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Propriétaire</TableHead>
                        <TableHead>Chevaux</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Facturation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...new Set(chevaux.map(c => c.proprietaire_nom))].map((proprio) => {
                        const chevauxProprio = chevaux.filter(c => c.proprietaire_nom === proprio);
                        const premierCheval = chevauxProprio[0];
                        return (
                          <TableRow key={proprio}>
                            <TableCell className="font-medium">
                              {proprio} {premierCheval?.proprietaire_prenom}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {chevauxProprio.map(c => (
                                  <Badge key={c.id} variant="outline" className="text-xs">
                                    {c.nom}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm">{premierCheval?.proprietaire_telephone}</p>
                              <p className="text-xs text-gray-500">{premierCheval?.proprietaire_email}</p>
                            </TableCell>
                            <TableCell>
                              <Button variant="outline" size="sm" onClick={() => setActiveTab('facturation')}>
                                Facturer
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Facturation Tab */}
        <TabsContent value="facturation" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-amber-600" />
                    Facturation
                  </CardTitle>
                  <CardDescription>Factures et paiements</CardDescription>
                </div>
                <Button className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle facture
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card className="bg-green-50">
                  <CardContent className="p-4">
                    <p className="text-sm text-green-700">Payées</p>
                    <p className="text-2xl font-bold text-green-900">
                      {factures.filter(f => f.statut === 'payee').reduce((sum, f) => sum + (f.montant || 0), 0).toLocaleString()} €
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-50">
                  <CardContent className="p-4">
                    <p className="text-sm text-yellow-700">En attente</p>
                    <p className="text-2xl font-bold text-yellow-900">
                      {factures.filter(f => f.statut === 'en_attente').reduce((sum, f) => sum + (f.montant || 0), 0).toLocaleString()} €
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="p-4">
                    <p className="text-sm text-red-700">En retard</p>
                    <p className="text-2xl font-bold text-red-900">
                      {factures.filter(f => f.statut === 'impayee').reduce((sum, f) => sum + (f.montant || 0), 0).toLocaleString()} €
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>N° Facture</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Cheval(aux)</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {factures.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Aucune facture générée
                      </TableCell>
                    </TableRow>
                  ) : (
                    factures.map((facture) => (
                      <TableRow key={facture.id}>
                        <TableCell className="font-medium">{facture.numero || facture.id?.slice(0, 8)}</TableCell>
                        <TableCell>{facture.client_nom}</TableCell>
                        <TableCell>{facture.chevaux?.join(', ') || '-'}</TableCell>
                        <TableCell className="font-medium">{facture.montant?.toLocaleString()} €</TableCell>
                        <TableCell>{facture.date ? new Date(facture.date).toLocaleDateString('fr-FR') : '-'}</TableCell>
                        <TableCell>
                          <Badge variant={
                            facture.statut === 'payee' ? 'default' :
                            facture.statut === 'en_attente' ? 'secondary' :
                            'destructive'
                          } className={facture.statut === 'payee' ? 'bg-green-100 text-green-800' : ''}>
                            {facture.statut === 'payee' ? 'Payée' : 
                             facture.statut === 'en_attente' ? 'En attente' : 'Impayée'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Cheval Detail Dialog */}
      {selectedCheval && (
        <Dialog open={!!selectedCheval} onOpenChange={() => setSelectedCheval(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HorseIcon className="h-6 w-6 text-amber-600" />
                {selectedCheval.nom}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label className="text-gray-500">Race</Label>
                <p>{selectedCheval.race || '-'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Robe</Label>
                <p>{selectedCheval.robe || '-'}</p>
              </div>
              <div>
                <Label className="text-gray-500">N° SIRE</Label>
                <p>{selectedCheval.sire || '-'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Sexe</Label>
                <p>{selectedCheval.sexe || '-'}</p>
              </div>
              <div className="col-span-2 border-t pt-4">
                <Label className="text-gray-500">Propriétaire</Label>
                <p className="font-medium">{selectedCheval.proprietaire_nom} {selectedCheval.proprietaire_prenom}</p>
                <p className="text-sm">{selectedCheval.proprietaire_telephone}</p>
                <p className="text-sm text-gray-500">{selectedCheval.proprietaire_email}</p>
              </div>
              <div>
                <Label className="text-gray-500">Hébergement</Label>
                <p>{selectedCheval.type_pension} - {selectedCheval.box_numero}</p>
              </div>
              <div>
                <Label className="text-gray-500">Tarif mensuel</Label>
                <p className="font-medium">{selectedCheval.tarif_mensuel || 350} €</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedCheval(null)}>Fermer</Button>
              <Button className="bg-amber-600 hover:bg-amber-700">
                <Edit2 className="h-4 w-4 mr-2" />
                Modifier
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
